import React, { useState } from 'react';
import { showError, showSuccess } from '../../utils/sweetAlert';

const CustomOrderForm = ({ product, selectedVariant, onClose, initialQuantity = 1 }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    instructions: '',
    printPosition: 'front',
    printSize: 'medium',
    quantity: initialQuantity
  });
  const [customImage, setCustomImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const printPositions = [
    { value: 'front', label: 'Frente', icon: 'fa-shirt' },
    { value: 'back', label: 'Espalda', icon: 'fa-shirt' },
    { value: 'left-sleeve', label: 'Manga Izquierda', icon: 'fa-hand' },
    { value: 'right-sleeve', label: 'Manga Derecha', icon: 'fa-hand' },
    { value: 'pocket', label: 'Bolsillo', icon: 'fa-square' },
    { value: 'full', label: 'Completo', icon: 'fa-expand' }
  ];

  const printSizes = [
    { value: 'small', label: 'Pequeño (10x10cm)' },
    { value: 'medium', label: 'Mediano (20x20cm)' },
    { value: 'large', label: 'Grande (30x30cm)' },
    { value: 'extra-large', label: 'Extra Grande (40x40cm)' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        showError('Solo se permiten imágenes (JPEG, PNG, WebP, GIF)');
        return;
      }
      
      // Validar tamaño (10MB máximo)
      if (file.size > 10 * 1024 * 1024) {
        showError('La imagen no puede superar los 10MB');
        return;
      }

      setCustomImage(file);
      
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.customerName.trim()) {
      showError('Por favor ingresa tu nombre');
      return;
    }
    if (!formData.customerEmail.trim()) {
      showError('Por favor ingresa tu email');
      return;
    }
    if (!customImage) {
      showError('Por favor sube una imagen para el estampado');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('productId', product._id);
      submitData.append('customerName', formData.customerName);
      submitData.append('customerEmail', formData.customerEmail);
      submitData.append('customerPhone', formData.customerPhone);
      submitData.append('instructions', formData.instructions);
      submitData.append('printPosition', formData.printPosition);
      submitData.append('printSize', formData.printSize);
      submitData.append('quantity', formData.quantity);
      submitData.append('customImage', customImage);
      
      if (selectedVariant) {
        submitData.append('selectedSize', selectedVariant.size || '');
        submitData.append('selectedColor', selectedVariant.color || '');
        submitData.append('selectedSku', selectedVariant.sku || '');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/custom-orders`, {
        method: 'POST',
        body: submitData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al enviar el pedido');
      }

      showSuccess('¡Pedido personalizado enviado! Te contactaremos pronto para confirmar los detalles.');
      
      // Limpiar formulario
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        instructions: '',
        printPosition: 'front',
        printSize: 'medium',
        quantity: 1
      });
      setCustomImage(null);
      setImagePreview(null);
      
      if (onClose) onClose();

    } catch (error) {
      console.error('Error:', error);
      showError(error.message || 'Error al enviar el pedido');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="custom-order-form-container">
      <div className="custom-order-header">
        <h3>
          <i className="fa-solid fa-pen-ruler me-2"></i>
          Personaliza tu Producto
        </h3>
        <p>Sube tu imagen y te la estampamos en el producto</p>
      </div>

      <form onSubmit={handleSubmit} className="custom-order-form">
        {/* Sección de imagen */}
        <div className="form-section">
          <h4>
            <i className="fa-solid fa-image me-2"></i>
            Tu Imagen para el Estampado
          </h4>
          
          <div className="image-upload-area">
            {imagePreview ? (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview" />
                <button 
                  type="button" 
                  className="remove-image-btn"
                  onClick={() => {
                    setCustomImage(null);
                    setImagePreview(null);
                  }}
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>
            ) : (
              <label className="upload-label">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageChange}
                  hidden
                />
                <div className="upload-placeholder">
                  <i className="fa-solid fa-cloud-upload-alt"></i>
                  <span>Haz clic o arrastra tu imagen aquí</span>
                  <small>JPEG, PNG, WebP o GIF (máx. 10MB)</small>
                </div>
              </label>
            )}
          </div>

          {/* Enlace para crear imagen con IA */}
          <div className="ai-generator-link">
            <span>¿No tienes una imagen?</span>
            <span className="ai-link-btn" style={{ opacity: 0.5, cursor: 'default' }}>
              <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
              Próximamente: Crear con IA
            </span>
          </div>
        </div>

        {/* Opciones de estampado */}
        <div className="form-section">
          <h4>
            <i className="fa-solid fa-sliders me-2"></i>
            Opciones de Estampado
          </h4>
          
          <div className="form-row">
            <div className="form-group">
              <label>Posición del Estampado</label>
              <div className="position-options">
                {printPositions.map(pos => (
                  <button
                    key={pos.value}
                    type="button"
                    className={`position-btn ${formData.printPosition === pos.value ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, printPosition: pos.value }))}
                  >
                    <i className={`fa-solid ${pos.icon}`}></i>
                    <span>{pos.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Tamaño del Estampado</label>
              <select
                name="printSize"
                value={formData.printSize}
                onChange={handleInputChange}
                className="form-select"
              >
                {printSizes.map(size => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Cantidad</label>
              <div className="quantity-input">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    quantity: Math.max(1, prev.quantity - 1) 
                  }))}
                >
                  <i className="fa-solid fa-minus"></i>
                </button>
                <span>{formData.quantity}</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    quantity: prev.quantity + 1 
                  }))}
                >
                  <i className="fa-solid fa-plus"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Datos de contacto */}
        <div className="form-section">
          <h4>
            <i className="fa-solid fa-user me-2"></i>
            Tus Datos de Contacto
          </h4>
          
          <div className="form-row">
            <div className="form-group">
              <label>Nombre Completo *</label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleInputChange}
                placeholder="Tu nombre"
                required
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="customerEmail"
                value={formData.customerEmail}
                onChange={handleInputChange}
                placeholder="tu@email.com"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Teléfono (opcional)</label>
              <input
                type="tel"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleInputChange}
                placeholder="+598 99 123 456"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Instrucciones Adicionales (opcional)</label>
            <textarea
              name="instructions"
              value={formData.instructions}
              onChange={handleInputChange}
              placeholder="Ej: Quiero el logo centrado, con fondo transparente..."
              rows={3}
            />
          </div>
        </div>

        {/* Resumen y botón de envío */}
        <div className="form-section summary-section">
          <div className="order-summary">
            <div className="summary-item">
              <span>Producto:</span>
              <strong>{product.name}</strong>
            </div>
            {selectedVariant && (
              <div className="summary-item">
                <span>Variante:</span>
                <strong>{selectedVariant.size} - {selectedVariant.color}</strong>
              </div>
            )}
            <div className="summary-item">
              <span>Cantidad:</span>
              <strong>{formData.quantity}</strong>
            </div>
            <div className="summary-item total">
              <span>Precio estimado:</span>
              <strong>${((product.basePrice || product.price) * formData.quantity).toLocaleString()}</strong>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting || !customImage}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin me-2"></i>
                Enviando...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane me-2"></i>
                Enviar Pedido Personalizado
              </>
            )}
          </button>

          <p className="form-note">
            <i className="fa-solid fa-info-circle me-1"></i>
            Te contactaremos para confirmar los detalles y el pago antes de procesar tu pedido.
          </p>
        </div>
      </form>
    </div>
  );
};

export default CustomOrderForm;
